import { EventRoot, SectionGrid, GridSpan, StickyWrapper } from './styles';
import {
  PublicSessionItem,
  PublicTrackItem,
} from '@hubs101/booking-api-skd-client/lib/types';
import { extractSearchParams, getImageSrc } from '@utils/fn';
import { getEventDetails } from '@store/slices/eventSlice';
import { useSelector } from 'react-redux';
import { useEffect, useMemo, useState } from 'react';
import { useHookParams, useMobileWidth, useSlug } from '@utils/hooks';
import {
  usePublicAllAgendas,
  usePublicCoLocatedEventsByEventId,
} from '@utils/apiHooks';
import Button from '@components/Button';
import AgendaDates from '@composites/AgendaDates';
import { AgendaDays } from './helper';
import AgendaItems from '@composites/AgendaItems';
import { adminEventsRouteFormat, EVENTS_PATH } from '@constants/paths';
import Filters from '@composites/Filters';
import LoadingFallBack from '@routes/layouts/components/LoadingFallback';
import { SCROLL_DOWN_MODE } from '@utils/constants';
import ScrollToTop from '@components/ScrollToTop';
import dayjs from 'dayjs';
import { getUniqueDaysTimezoneIndependent } from '@utils/time';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MetaTags } from '@components/MetaTags';
import HeroSection from '@composites/HeroSection';
import { useTheme } from 'styled-components';
import { IllustratedTextContent } from '@composites/IllustratedTextContent';
import IllustratedText from '@composites/IllustratedText';

export const Agenda = () => {
  const { t } = useTranslation();
  const eventSlug = useSlug(EVENTS_PATH) as string;
  const sluggedEvent = useSelector(getEventDetails)?.[0];
  const { pathname } = useLocation();
  const slug = pathname.split('/').filter(Boolean).pop() || '';
  const [day] = useHookParams(['day']);
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedEvents, setSelectedEvents] = useState<string[]>([eventSlug]);
  const isMobile = useMobileWidth();
  const theme = useTheme();
  const navigate = useNavigate();

  useEffect(() => {
    if (eventSlug) {
      setSelectedEvents([eventSlug]);
    }
  }, [eventSlug]);

  const { data: coLocatedEvents, isLoading: isCoLocatedEventsLoading } =
    usePublicCoLocatedEventsByEventId({
      eventId: sluggedEvent?.id,
      enabled: sluggedEvent?.id?.length > 0 && sluggedEvent?.colocated,
    });

  const coLocatedEventsItems = useMemo(() => {
    const items = [];

    // Add the current event first
    if (sluggedEvent?.slug && sluggedEvent?.name) {
      items.push({
        id: sluggedEvent.id,
        key: sluggedEvent.slug,
        value: sluggedEvent.name,
        color: sluggedEvent.design?.themeColor,
      });
    }

    // Add co-located events
    if (coLocatedEvents) {
      coLocatedEvents.forEach(event => {
        // Skip if it's the same as the current event
        if (event?.slug !== sluggedEvent?.slug) {
          items.push({
            id: event?.id,
            key: event?.slug,
            value: event?.name,
            color: event?.design?.themeColor,
          });
        }
      });
    }

    return items;
  }, [
    coLocatedEvents,
    sluggedEvent?.id,
    sluggedEvent?.slug,
    sluggedEvent?.name,
    sluggedEvent?.design?.themeColor,
  ]);

  // Create mapping between event slugs and event IDs for filtering
  const eventSlugToIdMapping = useMemo(() => {
    const mapping: Record<string, string> = {};
    if (coLocatedEvents) {
      coLocatedEvents.forEach(event => {
        mapping[event.slug] = event.id;
      });
    }
    // Add the main event
    if (sluggedEvent?.slug && sluggedEvent?.id) {
      mapping[sluggedEvent.slug] = sluggedEvent.id;
    }
    return mapping;
  }, [coLocatedEvents, sluggedEvent?.slug, sluggedEvent?.id]);

  // Convert selected event slugs to event IDs for filtering
  const selectedEventIds = useMemo(() => {
    return selectedEvents
      .map(slug => eventSlugToIdMapping[slug])
      .filter(Boolean);
  }, [selectedEvents, eventSlugToIdMapping]);

  const colocatedReady = typeof sluggedEvent?.colocated === 'boolean';

  const agendaParams = useMemo(
    () => ({
      page: 0,
      language: sluggedEvent?.language,
      eventId: sluggedEvent?.id,
      enabled: Boolean(sluggedEvent?.id && colocatedReady),
      colocated: Boolean(sluggedEvent?.colocated),
    }),
    [
      sluggedEvent?.language,
      sluggedEvent?.id,
      sluggedEvent?.colocated,
      colocatedReady,
    ]
  );

  const agendas: any = usePublicAllAgendas(agendaParams);

  useEffect(() => {
    if (sluggedEvent?.id && colocatedReady && agendas?.refetch) {
      agendas.refetch();
    }
  }, [sluggedEvent?.id, sluggedEvent?.colocated]);

  const agendasSessions = useMemo(() => {
    const raw = agendas?.data;

    const sessions: any[] = Array.isArray(raw)
      ? raw.map((e: any) => e?.sessions).flat()
      : (raw?.sessions ?? []);

    if (!sluggedEvent?.timezone) return [];

    return sessions
      .filter(e => Boolean(e))
      .map(e => ({
        ...e,
        start_date: dayjs
          .utc(e.start_date)
          .tz(sluggedEvent.timezone)
          .format('YYYY-MM-DDTHH:mm:ss.SSSSSSZ'),

        end_date: dayjs
          .utc(e.end_date)
          .tz(sluggedEvent.timezone)
          .format('YYYY-MM-DDTHH:mm:ss.SSSSSSZ'),
      }));
  }, [agendas?.data, sluggedEvent?.timezone]);

  const getSessionEventId = (s: any): string | undefined => {
    const e = s?.event;
    if (!e) return undefined;
    if (typeof e === 'string') return e;
    if (typeof e === 'object') return e.id || e.event_id || e.eventId;
    return undefined;
  };

  // 1) Allowed = current + colocated
  const allowedEventIds = useMemo(
    () =>
      [sluggedEvent?.id, ...(coLocatedEvents?.map(e => e.id) ?? [])].filter(
        Boolean
      ),
    [sluggedEvent?.id, coLocatedEvents]
  );

  const getPrevAgendaId = (e: any): string | undefined => {
    return (
      e?.previous_agenda_event_id || // new field
      (typeof e?.previous === 'string' ? e.previous : undefined) ||
      undefined
    );
  };

  const ownerToPrev = useMemo(() => {
    const m: Record<string, string | undefined> = {};

    (coLocatedEvents ?? []).forEach((ev: any) => {
      if (ev?.id) m[ev.id] = getPrevAgendaId(ev);
    });

    return m;
  }, [coLocatedEvents, sluggedEvent?.id, sluggedEvent]);

  const effectiveMap = useMemo(() => {
    const m: Record<string, string[]> = {};
    (allowedEventIds ?? []).forEach(ownerId => {
      if (!ownerId) return;
      const prev = ownerToPrev[ownerId];
      m[ownerId] = prev ? [prev] : [ownerId];
    });

    return m;
  }, [allowedEventIds, ownerToPrev, selectedEventIds]);

  const selectedIdsForFilter = useMemo(() => {
    const out = new Set<string>();
    (selectedEventIds ?? []).forEach(id => {
      const mapped = effectiveMap[id];
      if (mapped?.length) mapped.forEach(x => out.add(x));
    });
    return Array.from(out);
  }, [selectedEventIds, effectiveMap]);

  const correctedSessions = useMemo(() => {
    if (!Array.isArray(agendasSessions) || agendasSessions.length === 0) {
      return agendasSessions;
    }

    const start = dayjs(sluggedEvent?.start_date).tz(sluggedEvent?.timezone);
    const end = dayjs(sluggedEvent?.end_date).tz(sluggedEvent?.timezone);
    if (!start.isValid() || !end.isValid()) return agendasSessions;

    const eventDays: string[] = [];
    for (
      let d = start.startOf('day');
      d.isBefore(end, 'day') || d.isSame(end, 'day');
      d = d.add(1, 'day')
    ) {
      eventDays.push(d.format('YYYY-MM-DD'));
    }
    const k = eventDays.length;
    if (!k) return agendasSessions;

    const toYMD = (iso?: string) =>
      dayjs(iso).tz(sluggedEvent?.timezone).format('YYYY-MM-DD');

    const srcDaysByEvent = new Map<string, string[]>();
    for (const s of agendasSessions) {
      const ev = getSessionEventId(s) ?? sluggedEvent?.id ?? 'unknown';
      const key = toYMD(s.start_date);
      if (!srcDaysByEvent.has(ev)) srcDaysByEvent.set(ev, []);
      const arr = srcDaysByEvent.get(ev)!;
      if (!arr.includes(key)) arr.push(key);
    }

    for (const [ev, arr] of srcDaysByEvent) {
      arr.sort(); // ASC
    }

    const mapPerEvent = new Map<string, Map<string, string>>();

    for (const [ev, arr] of srcDaysByEvent) {
      const m = new Map<string, string>();

      const n = arr.length;
      const offset = Math.max(0, n - k);

      for (let i = 0; i < k && offset + i < n; i++) {
        m.set(arr[offset + i], eventDays[i]);
      }

      mapPerEvent.set(ev, m);
    }

    const nearestIndex = (arr: string[], src: string) => {
      if (!arr.length) return 0;

      let bestIdx = 0;
      let bestDiff = Math.abs(
        new Date(`${src}T00:00:00`).getTime() -
          new Date(`${arr[0]}T00:00:00`).getTime()
      );

      for (let i = 1; i < arr.length; i++) {
        const diff = Math.abs(
          new Date(`${src}T00:00:00`).getTime() -
            new Date(`${arr[i]}T00:00:00`).getTime()
        );
        if (diff < bestDiff) {
          bestDiff = diff;
          bestIdx = i;
        }
      }

      return bestIdx;
    };

    const mapped = agendasSessions.map(s => {
      const ev = getSessionEventId(s) ?? sluggedEvent?.id ?? 'unknown';
      const srcKey = toYMD(s.start_date);

      const perEvent = mapPerEvent.get(ev);
      const arr = srcDaysByEvent.get(ev) ?? [];
      let targetDay = perEvent?.get(srcKey);

      if (!targetDay) {
        const idx = nearestIndex(arr, srcKey);
        targetDay = eventDays[Math.min(idx, k - 1)] ?? eventDays[0];
      }

      // === Pure string manipulation ===
      // s.start_date = "2026-11-25T20:00:00.000-08:00" or "2026-11-25T20:00:00.000"
      const [origDatePart, timePartWithOffset] = s.start_date.split('T');

      // Split time from offset if any
      const match = timePartWithOffset.match(
        /^(\d{2}:\d{2}:\d{2}(?:\.\d+)?)((?:[+-]\d{2}:\d{2})?)$/
      );
      const timePart = match ? match[1] : '00:00:00.000';
      const offsetPart = match ? match[2] : '';

      // Calculate duration in minutes manually
      const [startHour, startMin, startSec]: number[] = timePart
        .split(/[:.]/)
        .map(Number);
      const [endHour, endMin, endSec]: number[] = s.end_date
        .split('T')[1]
        .split(/[:.]/)
        .map(Number);

      let durationMin = (endHour - startHour) * 60 + (endMin - startMin);
      if (durationMin < 0) {
        durationMin += 24 * 60; // fix overnight sessions
      }

      // Build end manually
      let endHourCorrected = startHour + Math.floor(durationMin / 60);
      let endMinCorrected = startMin + (durationMin % 60);

      if (endMinCorrected >= 60) {
        endHourCorrected += 1;
        endMinCorrected -= 60;
      }

      // Wrap hours for next day
      if (endHourCorrected >= 24) {
        endHourCorrected -= 24;
      }
      const pad = (n: number) => n.toString().padStart(2, '0');
      // eslint-disable-next-line
      const correctedStart = `${targetDay}T${pad(startHour)}:${pad(startMin)}:${pad(startSec || 0)}${offsetPart}`;
      // eslint-disable-next-line
      const correctedEnd = `${targetDay}T${pad(endHourCorrected)}:${pad(endMinCorrected)}:${pad(endSec || 0)}${offsetPart}`;

      return {
        ...s,
        start_date: correctedStart,
        end_date: correctedEnd,
      } as PublicSessionItem;
    });

    // --- Sorting ---
    mapped.sort((a, b) => {
      const dateA = dayjs(a.start_date);
      const dateB = dayjs(b.start_date);
      if (dateA.isBefore(dateB)) return -1;
      if (dateA.isAfter(dateB)) return 1;

      // If start_date is equal, sort by priority (lower first)
      const priorityA = typeof a.priority === 'number' ? a.priority : 9999;
      const priorityB = typeof b.priority === 'number' ? b.priority : 9999;
      return priorityA - priorityB;
    });

    const toNum = (v: any) => {
      const n =
        typeof v === 'number'
          ? v
          : typeof v === 'string'
            ? parseInt(v, 10)
            : NaN;
      return Number.isFinite(n) ? n : Number.POSITIVE_INFINITY;
    };

    const sortSpeakersByPriorityAsc = (speakers: any[]) =>
      [...speakers].sort(
        (a, b) =>
          toNum(a?.priority ?? a?.order ?? a?.sort_order ?? a?.sortOrder) -
          toNum(b?.priority ?? b?.order ?? b?.sort_order ?? b?.sortOrder)
      );

    const mappedWithSortedSpeakers = mapped.map(s => ({
      ...s,
      speakers: Array.isArray(s?.speakers)
        ? sortSpeakersByPriorityAsc(s.speakers)
        : s?.speakers,
    }));

    return mappedWithSortedSpeakers;
  }, [agendasSessions, sluggedEvent?.start_date, sluggedEvent?.end_date]);

  const agendasTracks = useMemo(() => {
    const raw = agendas?.data;
    if (Array.isArray(raw)) {
      return raw
        .map((e: any) => e?.tracks)
        .flat()
        .filter(Boolean) as PublicTrackItem[];
    }
    return (raw?.tracks ?? []) as PublicTrackItem[];
  }, [agendas?.data]);

  const agendasIsLoading = useMemo(() => agendas?.isLoading, [agendas]);

  const isLoading = agendasIsLoading || isCoLocatedEventsLoading;

  const enableEventFilter = Boolean(sluggedEvent?.colocated);

  const sessionsForTabs = useMemo(() => {
    if (!Array.isArray(correctedSessions)) return [];
    if (!enableEventFilter || !selectedIdsForFilter?.length)
      return correctedSessions;

    return correctedSessions.filter(s => {
      const ev = s?.event;
      const evId =
        typeof ev === 'string' ? ev : ev?.id || ev?.event_id || ev?.eventId;
      return evId ? selectedIdsForFilter.includes(evId) : true;
    });
  }, [correctedSessions, enableEventFilter, selectedIdsForFilter]);

  const agendaDaysFormat = useMemo(
    () =>
      getUniqueDaysTimezoneIndependent(
        (sessionsForTabs ?? []).map(e => e?.start_date)
      ),
    [sessionsForTabs]
  );
  const activeDay = useMemo<string>(() => {
    // if day in url
    if (day && agendaDaysFormat?.includes(day)) return day;

    const days = agendaDaysFormat ?? [];
    if (days.length === 0) return '';
    if (days.length === 1) return days[0];

    // if today or in future -> 2nd tab
    const startDay = dayjs(sluggedEvent?.start_date).startOf('day');
    const today = dayjs().startOf('day');

    if (today.isSame(startDay) || today.isBefore(startDay)) {
      return days[0];
    } else return days[days.length - 1];
  }, [day, agendaDaysFormat, sluggedEvent?.start_date]);

  const sortedAgendaDaysFormat = useMemo(
    () =>
      [
        ...(AgendaDays?.filter(
          (e, index) => index !== agendaDaysFormat.indexOf(activeDay)
        ).slice(0, agendaDaysFormat?.length - 1) ?? []),
        AgendaDays?.find(
          (e, index) => index === agendaDaysFormat.indexOf(activeDay)
        ) ?? '',
      ]?.filter(e => e?.length),
    [activeDay, agendaDaysFormat]
  );

  useEffect(() => {
    if (activeDay && day !== activeDay) {
      setSearchParams({
        ...extractSearchParams(searchParams),
        day: activeDay,
      });
    }
  }, [activeDay, day, searchParams, setSearchParams]);

  const content = (
    <IllustratedTextContent
      titleColor={sluggedEvent?.design?.headerFontColor}
      titleText={t('want-to-join-as-an-exhibitor-or-sponsor-title')}
      subtitleText={t('want-to-join-as-an-exhibitor-or-sponsor-text')}
      themeColor={sluggedEvent?.design?.themeColor}
      orientation={'left'}
      button={
        <Button
          title={t('want-to-join-as-an-exhibitor-or-sponsor-button')}
          backgroundColor={sluggedEvent?.design?.themeColor}
          hoverBackgroundColor={theme.color.mobilityGrey}
          textColor={theme.color.white}
          borderRadius={theme.borderRadius.radius10}
          fontSize={theme.fontSize.size20}
          lineHeight={theme.lineHeight.height150}
          padding={isMobile ? '1.5rem 2.5rem' : '1.1rem 4.9rem'}
          width="100%"
          onClick={() => {
            navigate(
              adminEventsRouteFormat(sluggedEvent?.slug, t('bp-packages-url'))
            );
          }}
        />
      }
    />
  );

  if (isLoading) return <LoadingFallBack />;

  return (
    <>
      <MetaTags
        title={`${sluggedEvent?.name} ${slug?.split('-')?.join(' ')}`}
        description={`${sluggedEvent?.name} ${slug?.split('-')?.join(' ')}`}
        keywords={`${sluggedEvent?.slug}, ${slug?.split('-')?.join(', ')}`}
        image={getImageSrc(sluggedEvent?.design?.eventLogoPosSvg)}
        url={window.location.href}
        twitterCardType="summary"
        type="product"
      />
      <EventRoot>
        <SectionGrid>
          <GridSpan span={12}>
            <HeroSection
              backgroundImage={getImageSrc(
                'https://media.we-conect.com/images/website/agenda_hero_bg.png',
                sluggedEvent?.portfolio?.slug
              )}
              gradientOverlay={true}
              title={t('agenda-hero-title')}
              description={t('agenda-hero-description')}
              descriptionFontWeight={
                isMobile
                  ? theme.fontWeight.weight500
                  : theme.fontWeight.weight700
              }
              buttonText={t('agenda-hero-btn')}
              buttonURL={`${window.location.origin}/events/${sluggedEvent?.slug}${t('book-now-url')}`}
              openInNewTab={false}
              height={isMobile ? '535px' : '700px'}
            />
          </GridSpan>

          <GridSpan span={12}>
            <AgendaDates
              agendaDaysFormat={agendaDaysFormat}
              activeDay={activeDay}
            />
          </GridSpan>

          <GridSpan start={2} span={10}>
            <StickyWrapper>
              <Filters
                sessions={correctedSessions}
                agendasTracks={agendasTracks}
                sortedAgendaDaysFormat={sortedAgendaDaysFormat}
                agendaDaysFormat={agendaDaysFormat}
                activeDay={activeDay}
                coLocatedEventsItems={coLocatedEventsItems}
                setSelectedEvents={setSelectedEvents}
                selectedEvents={selectedEvents}
              />

              <AgendaItems
                key={sluggedEvent?.id}
                agendaDaysFormat={agendaDaysFormat}
                activeDay={activeDay}
                agendasSessions={correctedSessions}
                agendasTracks={agendasTracks}
                agendasIsLoading={agendasIsLoading}
                selectedEventIds={selectedIdsForFilter}
                topLevelSelectedEventIds={selectedEventIds}
                coLocatedEventsItems={coLocatedEventsItems}
                enableEventFilter={enableEventFilter}
                eventIdMapping={effectiveMap}
              />
            </StickyWrapper>
          </GridSpan>

          <GridSpan span={12}>
            <IllustratedText
              images={[
                getImageSrc(
                  'https://media.we-conect.com/images/website/agenda_partnering.jpg',
                  sluggedEvent?.portfolio?.slug
                ),
              ]}
              orientation={'right'}
              content={content}
              isLastItem={true}
            />
          </GridSpan>

          <GridSpan span={12}>
            <ScrollToTop mode={SCROLL_DOWN_MODE.DARK} />
          </GridSpan>
        </SectionGrid>
      </EventRoot>
    </>
  );
};

export default Agenda;
